// Gelo Minimal: Verhalten der eigenen Abschnitte (gelo-*), wie im Entwurf gelo.elevatem.de/minimal.
// 1. „In die Tasche“ direkt auf Karten und Zeilen (Formulare mit data-gelo-kauf): legt per Ajax in den Warenkorb und
//    meldet es Horizon über dasselbe Ereignis wie das Produktformular (CartLinesUpdateEvent), damit Warenkorb-Symbol und
//    -Schublade sich aktualisieren. Schlägt etwas fehl, schickt das Formular ganz normal ab (/cart/add → Warenkorb).
// 2. Plus auf Karten (unter 990 px): klappt die Größen/Farben in der Karte auf, Escape schließt.
// 3. Schaufenster (ab 990 px): Überfahren oder Fokus auf einer Zeile wechselt das Bild der Bühne.
// 4. Leisten: mit der Maus ziehen und mit den Pfeiltasten wischen, sobald die Leiste breiter ist als der Platz
//    (Handy, Desktop und Theme-Editor). Touch bleibt nativ. Betrifft .gm-zeilen, .gm-raster--leiste,
//    .gm-spot__farben--leiste, .gm-kreise, die Produktgalerie und den Farbvergleich.
// 5. „Code kopieren“ (Gut zu wissen).
import { CartLinesUpdateEvent } from '@shopify/events';

const DESK = matchMedia('(min-width: 990px)');
const RUHIG = matchMedia('(prefers-reduced-motion: reduce)');
const QUITTUNG_MS = 1600;
const LEISTEN = '.gm-zeilen, .gm-raster--leiste, .gm-spot__farben--leiste, .gm-kreise, .gm-bs-gal__set, .gm-bs-farben__liste';
const WISCH_STEUER = 'button, input, select, textarea, label, summary';

// ---------- Ansage für Screenreader (eine Stelle für die ganze Seite) ----------
let ansage;
function ansagen(text) {
  if (!ansage) {
    ansage = document.createElement('p');
    ansage.className = 'visually-hidden';
    ansage.setAttribute('aria-live', 'polite');
    document.body.append(ansage);
  }
  ansage.textContent = '';
  requestAnimationFrame(() => {
    ansage.textContent = text;
  });
}

function quittung(element) {
  element.classList.add('is-done');
  clearTimeout(element._geloQuittung);
  element._geloQuittung = setTimeout(() => element.classList.remove('is-done'), QUITTUNG_MS);
}

// ---------- 1. In die Tasche ----------
document.addEventListener('submit', async (ereignis) => {
  const form = ereignis.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-gelo-kauf')) return;
  ereignis.preventDefault();

  const knopf = ereignis.submitter instanceof HTMLButtonElement ? ereignis.submitter : form.querySelector('button');
  if (knopf?.getAttribute('aria-busy') === 'true') return;
  const daten = new FormData(form);
  if (knopf?.name === 'id') daten.set('id', knopf.value);
  const id = daten.get('id');
  if (!id) return;

  const versprechen = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: versprechen.promise,
    })
  );

  try {
    knopf?.setAttribute('aria-busy', 'true');
    const antwort = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: daten,
    });
    const ergebnis = await antwort.json();
    const korb = await (await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)).json();
    versprechen.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(korb),
      detail: { items: korb.items, source: 'gelo-kauf', itemCount: 1, didError: !antwort.ok },
    });
    if (!antwort.ok) throw new Error(ergebnis.description || ergebnis.message || 'Nicht möglich');
    bestaetigen(form, knopf);
  } catch (fehler) {
    versprechen.reject?.(fehler);
    form.removeAttribute('data-gelo-kauf');
    if (knopf?.name === 'id') {
      const feld = document.createElement('input');
      feld.type = 'hidden';
      feld.name = 'id';
      feld.value = knopf.value;
      form.append(feld);
    }
    form.submit();
  } finally {
    knopf?.removeAttribute('aria-busy');
  }
});

// Quittung: 2-px-Linie unter dem Knopf, der Text bleibt (nichts verschiebt sich); am Plus ein Haken.
function bestaetigen(form, knopf) {
  const name = form.dataset.name || '';
  ansagen(name ? `„${name}“ liegt in der Tasche.` : 'Liegt in der Tasche.');
  if (!knopf) return;
  if (knopf.classList.contains('gm-plus') || knopf.name === 'id') {
    quittung(knopf);
  } else {
    // Wort-Knopf „In die Tasche“: kurz „In der Tasche“, die Breite bleibt
    const vorher = knopf.textContent;
    knopf.style.minWidth = `${knopf.offsetWidth}px`;
    knopf.textContent = 'In der Tasche';
    quittung(knopf);
    setTimeout(() => {
      knopf.textContent = vorher;
      knopf.style.minWidth = '';
    }, QUITTUNG_MS);
  }
  // geöffnete Karte (Handy/Tablet): kurz stehen lassen, dann zuklappen und den Haken am Plus zeigen; lag der Fokus
  // in der Karte (Tastatur, Screenreader), geht er aufs Plus statt verloren (der Größen-Knopf verschwindet)
  const karte = knopf.closest('.gm-karte.is-offen');
  if (karte && !DESK.matches) {
    setTimeout(() => {
      kartenZu(karte, karte.contains(document.activeElement));
      const plus = karte.querySelector('.gm-plus');
      if (plus) quittung(plus);
    }, 600);
  }
}

// ---------- 2. Plus auf Karten ----------
function kartenZu(karte, fokus = false) {
  karte.classList.remove('is-offen');
  const plus = karte.querySelector('[data-gelo-plus]');
  plus?.setAttribute('aria-expanded', 'false');
  if (fokus) plus?.focus({ preventScroll: true });
}

document.addEventListener('click', (ereignis) => {
  const ziel = ereignis.target instanceof Element ? ereignis.target : null;
  const plus = ziel?.closest('[data-gelo-plus]');
  if (plus) {
    const karte = plus.closest('.gm-karte');
    if (!karte) return;
    const auf = !karte.classList.contains('is-offen');
    document.querySelectorAll('.gm-karte.is-offen').forEach((andere) => andere !== karte && kartenZu(andere));
    karte.classList.toggle('is-offen', auf);
    plus.setAttribute('aria-expanded', String(auf));
    if (auf) karte.querySelector('.gm-kauf .gm-chip:not([disabled])')?.focus({ preventScroll: true });
    return;
  }
  const kopieren = ziel?.closest('[data-gelo-code]');
  if (kopieren) codeKopieren(kopieren);
});

document.addEventListener('keydown', (ereignis) => {
  if (ereignis.key !== 'Escape' || !(ereignis.target instanceof Element)) return;
  const karte = ereignis.target.closest('.gm-karte.is-offen');
  if (karte) kartenZu(karte, true);
});

// ---------- 3. Schaufenster: Zeile ↔ Bild der Bühne ----------
function fensterStart(fenster) {
  const buehne = fenster.querySelector('[data-gelo-buehne]');
  const zeilen = [...fenster.querySelectorAll('.gm-zeile')];
  const bilder = buehne ? [...buehne.children] : [];
  if (!buehne || zeilen.length < 2 || bilder.length !== zeilen.length) return;
  let aktiv = 0;

  const aktivieren = (i) => {
    if (i === aktiv) return;
    const alt = bilder[aktiv];
    const neu = bilder[i];
    alt.classList.remove('is-oben');
    neu.classList.add('is-aktiv', 'is-oben');
    // erst im übernächsten Frame sichtbar machen, sonst springt die Überblendung
    requestAnimationFrame(() => requestAnimationFrame(() => neu.classList.add('is-sichtbar')));
    // das alte Bild bleibt darunter stehen, bis das neue überblendet ist (kein Grau dazwischen)
    setTimeout(() => {
      if (bilder[aktiv] !== alt) alt.classList.remove('is-aktiv', 'is-sichtbar');
    }, RUHIG.matches ? 0 : 280);
    zeilen[aktiv].classList.remove('is-aktiv');
    zeilen[i].classList.add('is-aktiv');
    buehne.href = zeilen[i].dataset.url;
    buehne.setAttribute('aria-label', `${zeilen[i].dataset.name} ansehen`);
    aktiv = i;
  };

  zeilen.forEach((zeile, i) => {
    zeile.addEventListener('mouseenter', () => DESK.matches && aktivieren(i));
    zeile.addEventListener('focusin', () => DESK.matches && aktivieren(i));
  });
  // Beim ersten Überfahren der Liste alle Bühnenbilder vorladen
  fenster.querySelector('.gm-zeilen')?.addEventListener(
    'mouseenter',
    () => buehne.querySelectorAll('img[loading="lazy"]').forEach((bild) => (bild.loading = 'eager')),
    { once: true }
  );
}

// ---------- 4. Leisten: ziehen, Pfeile, Tastatur (nur wenn die Leiste wirklich überläuft) ----------
function ueberlaeuft(leiste) {
  const art = getComputedStyle(leiste).overflowX;
  return (art === 'auto' || art === 'scroll') && leiste.scrollWidth > leiste.clientWidth + 1;
}

function wischSchritt(leiste) {
  const kind = leiste.firstElementChild;
  if (!kind) return Math.max(120, leiste.clientWidth * 0.8);
  return kind.getBoundingClientRect().width + (parseFloat(getComputedStyle(leiste).columnGap) || 0);
}

function wischBewegen(leiste, richtung) {
  leiste.scrollBy({
    left: richtung * wischSchritt(leiste),
    behavior: RUHIG.matches ? 'auto' : 'smooth',
  });
}

function pfeilKnopf(richtung) {
  const knopf = document.createElement('button');
  knopf.type = 'button';
  knopf.className = `gm-wisch__pfeil gm-wisch__pfeil--${richtung}`;
  knopf.setAttribute('aria-label', richtung === 'vor' ? 'Weiter' : 'Zurück');
  knopf.innerHTML =
    richtung === 'vor'
      ? '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false"><path d="M7.5 4.5 13 10l-5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.25"/></svg>'
      : '<svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12.5 4.5 7 10l5.5 5.5" fill="none" stroke="currentColor" stroke-width="1.25"/></svg>';
  return knopf;
}

function pfeileSicher(leiste) {
  const ort = leiste.parentElement;
  if (!ort) return;
  ort.classList.add('gm-wisch-ort');
  if (!leiste._gmZurueck) {
    const zurueck = pfeilKnopf('zurueck');
    const vor = pfeilKnopf('vor');
    leiste.before(zurueck);
    leiste.after(vor);
    zurueck.addEventListener('click', () => wischBewegen(leiste, -1));
    vor.addEventListener('click', () => wischBewegen(leiste, 1));
    leiste.addEventListener('scroll', () => pfeileStand(leiste), { passive: true });
    leiste._gmZurueck = zurueck;
    leiste._gmVor = vor;
  }
  pfeileStand(leiste);
}

function pfeileStand(leiste) {
  const kann = ueberlaeuft(leiste);
  const links = leiste.scrollLeft > 2;
  const rechts = leiste.scrollWidth - leiste.clientWidth - leiste.scrollLeft > 2;
  const mitte = `${leiste.offsetTop + leiste.offsetHeight / 2}px`;
  for (const knopf of [leiste._gmZurueck, leiste._gmVor]) {
    if (!knopf) continue;
    knopf.style.top = mitte;
  }
  if (leiste._gmZurueck) leiste._gmZurueck.hidden = !kann || !links;
  if (leiste._gmVor) leiste._gmVor.hidden = !kann || !rechts;
  leiste.classList.toggle('gm-wischbar', kann);
  if (kann) leiste.setAttribute('tabindex', '0');
  else if (!leiste.matches('.gm-bs-gal__set')) leiste.removeAttribute('tabindex');
}

function ziehenStart(leiste) {
  if (leiste.dataset.gmWisch === '1') return;
  leiste.dataset.gmWisch = '1';
  let zeiger = null;
  let startX = 0;
  let startY = 0;
  let startLinks = 0;
  let zieht = false;

  leiste.addEventListener('pointerdown', (ereignis) => {
    if (ereignis.pointerType === 'touch' || ereignis.button !== 0) return;
    if (!(ereignis.target instanceof Element) || ereignis.target.closest(WISCH_STEUER)) return;
    if (!ueberlaeuft(leiste)) return;
    zeiger = ereignis.pointerId;
    startX = ereignis.clientX;
    startY = ereignis.clientY;
    startLinks = leiste.scrollLeft;
    zieht = false;
  });

  leiste.addEventListener('pointermove', (ereignis) => {
    if (ereignis.pointerId !== zeiger) return;
    const dx = ereignis.clientX - startX;
    const dy = ereignis.clientY - startY;
    if (!zieht) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dx) <= Math.abs(dy)) {
        zeiger = null;
        return;
      }
      zieht = true;
      leiste.classList.add('is-zieht');
      leiste.setPointerCapture(ereignis.pointerId);
    }
    leiste.scrollLeft = startLinks - dx;
  });

  const ende = (ereignis) => {
    if (ereignis.pointerId !== zeiger) return;
    zeiger = null;
    if (!zieht) return;
    zieht = false;
    leiste.classList.remove('is-zieht');
    leiste.dataset.gmZog = '1';
  };
  leiste.addEventListener('pointerup', ende);
  leiste.addEventListener('pointercancel', ende);
  leiste.addEventListener('dragstart', (ereignis) => ereignis.preventDefault());
  leiste.addEventListener(
    'click',
    (ereignis) => {
      if (leiste.dataset.gmZog !== '1') return;
      delete leiste.dataset.gmZog;
      ereignis.preventDefault();
      ereignis.stopPropagation();
    },
    true
  );
}

function leistenPruefen(wurzel = document) {
  wurzel.querySelectorAll(LEISTEN).forEach((leiste) => {
    ziehenStart(leiste);
    pfeileSicher(leiste);
    if (!leiste._gmBeobachter && typeof ResizeObserver !== 'undefined') {
      leiste._gmBeobachter = new ResizeObserver(() => pfeileStand(leiste));
      leiste._gmBeobachter.observe(leiste);
    }
  });
}

document.addEventListener('keydown', (ereignis) => {
  if (ereignis.key !== 'ArrowLeft' && ereignis.key !== 'ArrowRight') return;
  if (!(ereignis.target instanceof Element)) return;
  if (ereignis.target.closest('input, textarea, select')) return;
  const leiste = ereignis.target.closest(LEISTEN);
  if (!leiste || !ueberlaeuft(leiste)) return;
  ereignis.preventDefault();
  wischBewegen(leiste, ereignis.key === 'ArrowRight' ? 1 : -1);
});

// ---------- 5. Code kopieren ----------
function codeStatus(knopf) {
  let el = knopf.parentElement;
  for (let i = 0; i < 4 && el; i += 1) {
    const status = el.querySelector('[role="status"]');
    if (status) return status;
    el = el.parentElement;
  }
  return null;
}

async function codeKopieren(knopf) {
  const code = knopf.dataset.geloCode || '';
  const meldung = codeStatus(knopf);
  let ok = false;
  try {
    await navigator.clipboard.writeText(code);
    ok = true;
  } catch {
    try {
      const feld = document.createElement('textarea');
      feld.value = code;
      feld.setAttribute('readonly', '');
      feld.style.position = 'fixed';
      feld.style.left = '-9999px';
      document.body.append(feld);
      feld.select();
      ok = document.execCommand('copy');
      feld.remove();
    } catch {
      ok = false;
    }
  }
  if (/^[A-Za-z0-9_-]+$/.test(code)) {
    const wurzel = window.Shopify?.routes?.root ?? '/';
    fetch(`${wurzel}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
  }
  if (!meldung) return;
  const zeile = knopf.closest('.gm-codezeile');
  if (zeile) zeile.classList.toggle('is-kopiert', ok);
  meldung.textContent = ok ? 'Code kopiert' : `Code: ${code}`;
  clearTimeout(meldung._geloZeit);
  meldung._geloZeit = setTimeout(() => {
    meldung.textContent = '';
    zeile?.classList.remove('is-kopiert');
  }, 3000);
}

// ---------- Start (auch nach dem Neuladen eines Abschnitts im Theme-Editor) ----------
function start(wurzel = document) {
  wurzel.querySelectorAll('[data-gelo-fenster]').forEach(fensterStart);
  leistenPruefen(wurzel);
}

start();
DESK.addEventListener('change', () => {
  if (DESK.matches) document.querySelectorAll('.gm-karte.is-offen').forEach((karte) => kartenZu(karte));
  leistenPruefen();
});
let groesse;
addEventListener('resize', () => {
  clearTimeout(groesse);
  groesse = setTimeout(leistenPruefen, 200);
});
document.addEventListener('shopify:section:load', (ereignis) => start(ereignis.target));
